import * as React from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { ErrorStateType } from '@models';
import {
  AuthenticationStatus,
  getProjectDepDefaultAuthRoute,
  IProjectPicker,
  RequestStatus,
  User,
} from '@store/types';
import { app as routes } from '@configs/routes.json';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@store/reducer';

interface LocationState {
  from: {
    pathname: string;
    search: string;
  };
}

interface RouteProps {
  children: React.ReactNode;
  authenticationStatus: AuthenticationStatus;
  path: string;
  errors: ErrorStateType[];
  user?: User;
}

export const defaultAnonymousRoute = (): string => routes.login;

export const showAlmostThere = (
  status: AuthenticationStatus,
  errors: ErrorStateType[],
  user?: User
): boolean => {
  return (
    status === AuthenticationStatus.authenticated &&
    // TODO: Do we need MissingAccountAssociation?
    (errors.includes(ErrorStateType.MissingAccountAssociation) ||
      user?.permissions.length === 0)
  );
};

export const acceptShowTermsOfService = (
  status: AuthenticationStatus,
  errors: ErrorStateType[]
): boolean => {
  return (
    status === AuthenticationStatus.authenticated &&
    errors.includes(ErrorStateType.InvalidTOS)
  );
};

export const AuthenticatedRoute: React.FC<RouteProps> = ({
  children,
  authenticationStatus,
  errors,
  user,
  ...rest
}) => {
  let redirect = false;
  let pathname = '';

  if (authenticationStatus !== AuthenticationStatus.authenticated) {
    redirect = true;
    pathname = defaultAnonymousRoute();
  }

  if (!redirect && showAlmostThere(authenticationStatus, errors, user)) {
    redirect = true;
    pathname = routes.almostThere;
  }

  if (!redirect && acceptShowTermsOfService(authenticationStatus, errors)) {
    redirect = true;
    pathname = routes.acceptTermsOfService;
  }

  return (
    <Route
      {...rest}
      render={({ location }) =>
        !redirect ? (
          children
        ) : (
          <Redirect
            to={{
              pathname,
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

export const AnonymousRoute: React.FC<RouteProps> = ({
  children,
  authenticationStatus,
  errors,
  user,
  ...rest
}) => {
  const location = useLocation<LocationState>();
  const projectPicker: IProjectPicker = useSelector(
    (state: IApplicationState) => state.projectPicker
  );
  let redirect = false,
    pathname = '',
    search = '';

  if (
    authenticationStatus === AuthenticationStatus.authenticated &&
    projectPicker.getProjectsStatus === RequestStatus.completed
  ) {
    // we don't know where to redirect to until we know what the project's tabs are
    redirect = true;
    pathname =
      location.state?.from.pathname ||
      getProjectDepDefaultAuthRoute(projectPicker);
    search = location.state?.from.search || '';
  } else if (
    authenticationStatus === AuthenticationStatus.authenticated &&
    showAlmostThere(authenticationStatus, errors, user)
  ) {
    // for almost there and accept TOS, we don't start the project picker
    redirect = true;
    pathname = routes.almostThere;
  } else if (
    authenticationStatus === AuthenticationStatus.authenticated &&
    acceptShowTermsOfService(authenticationStatus, errors)
  ) {
    redirect = true;
    pathname = routes.acceptTermsOfService;
  }

  return (
    <Route
      {...rest}
      render={({ location }) =>
        !redirect ? (
          children
        ) : (
          <Redirect
            to={{
              pathname,
              search,
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};
